import React, { useEffect, useState } from "react";
import { Box, Popover, Typography, Stack } from "@mui/material";
import moment from "moment";
import {
  startOfYear,
  endOfYear,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import "../../../styles/analytics/date-range.scss";

import DateRangePicker from "./date-range-picker.jsx";

const PeriodPickers = ({
  currentPeriodRange,
  comparisonPeriodRange,
  setCurrentPeriodRange,
  setComparisonPeriodRange,
  hidePeriodDiff,
  hideDivider,
  currentPeriod,
  setCurrentPeriod,
  comparePeriod,
  setComparePeriod,
}) => {
  const [tempCurrentPeriodRange, setTempCurrentPeriodRange] =
    useState(currentPeriodRange);
  const [tempComparisonPeriodRange, setTempComparisonPeriodRange] = useState(
    comparisonPeriodRange
  );
  const [isValidRange, setIsValidRange] = useState(true);

  useEffect(() => {
    if (currentPeriod !== "custom") {
      setTempCurrentPeriodRange(currentPeriodRange);
    }
  }, [currentPeriodRange]);

  useEffect(() => {
    if (currentPeriod !== "custom") {
      setTempComparisonPeriodRange(comparisonPeriodRange);
    }
  }, [comparisonPeriodRange]);

  useEffect(() => {
    if (
      currentPeriod === "custom" &&
      moment(tempCurrentPeriodRange.startDate).diff(
        moment(tempCurrentPeriodRange.endDate),
        "days"
      ) !== 0
    ) {
      setCurrentPeriodRange(tempCurrentPeriodRange);
    }
  }, [tempCurrentPeriodRange]);

  useEffect(() => {
    if (
      comparePeriod === "custom" &&
      moment(tempComparisonPeriodRange.startDate).diff(
        moment(tempComparisonPeriodRange.endDate),
        "days"
      ) !== 0
    ) {
      setComparisonPeriodRange(tempComparisonPeriodRange);
    }
  }, [tempComparisonPeriodRange]);

  const onChangeCurrentPeriod = (rangeType, periodType) => {
    const currentDate = new Date();
    let lastYearDate;

    if (rangeType === "currentPeriodRange") {
      setCurrentPeriod(periodType);
      switch (periodType) {
        case "currentMouthToDate":
          setCurrentPeriodRange({
            startDate: startOfMonth(currentDate),
            endDate: subDays(currentDate.setHours(23, 59, 59, 999), 1),
            key: "selection",
          });
          break;
        case "lastWeek":
          setCurrentPeriodRange({
            startDate: startOfWeek(subWeeks(currentDate, 1), {
              weekStartsOn: 1,
            }),
            endDate: endOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 }),
            key: "selection",
          });
          break;
        case "lastMonth":
          setCurrentPeriodRange({
            startDate: startOfMonth(subMonths(currentDate, 1)),
            endDate: endOfMonth(subMonths(currentDate, 1)),
            key: "selection",
          });
          break;

        case "lastYear":
          lastYearDate = currentDate;
          lastYearDate.setFullYear(currentDate.getFullYear() - 1);
          setCurrentPeriodRange({
            startDate: startOfYear(lastYearDate),
            endDate: endOfYear(lastYearDate),
            key: "selection",
          });
          break;
      }
    } else {
      setComparePeriod(periodType);
      switch (periodType) {
        case "previousMouthToDate":
          setComparisonPeriodRange({
            startDate: startOfMonth(subMonths(currentDate, 1)),
            endDate: subMonths(
              subDays(currentDate.setHours(23, 59, 59, 999), 1),
              1
            ),
            key: "selection",
          });
          break;
        case "previousWeek":
          setComparisonPeriodRange({
            startDate: startOfWeek(subWeeks(currentDate, 2), {
              weekStartsOn: 1,
            }),
            endDate: endOfWeek(subWeeks(currentDate, 2), {
              weekStartsOn: 1,
            }),
            key: "selection",
          });
          break;
        case "previousMouth":
          setComparisonPeriodRange({
            startDate: startOfMonth(subMonths(currentDate, 2)),
            endDate: endOfMonth(subMonths(currentDate, 2)),
            key: "selection",
          });
          break;

        case "previousYear":
          setComparisonPeriodRange({
            startDate: startOfYear(subYears(currentDate, 1)),
            endDate: endOfYear(subYears(currentDate, 1)),
            key: "selection",
          });
          break;

        case "none":
          setComparisonPeriodRange({
            startDate: null,
            endDate: null,
            key: "selections",
          });
          break;
      }
    }
  };

  return (
    <Stack direction="column">
      <Stack direction="row">
        <Box className="date-inputs-container" sx={{ paddingRight: "20px" }}>
          <DateRangePicker
            range={tempCurrentPeriodRange}
            setRange={setTempCurrentPeriodRange}
            period={currentPeriod}
            setPeriod={(period) =>
              onChangeCurrentPeriod("currentPeriodRange", period)
            }
            title={{
              label: "Current Period",
              markColor: "#1128c1",
            }}
            periodList={[
              {
                value: "custom",
                label: "Custom",
              },
              {
                value: "currentMouthToDate",
                label: (
                  <>
                    Month to date
                    <Typography
                      component="em"
                      sx={{
                        color: "#808080",
                        fontStyle: "italic",
                        fontSize: "12px",
                      }}
                    >
                      {" "}
                      (Default)
                    </Typography>
                  </>
                ),
              },
              {
                value: "lastWeek",
                label: "Last week",
              },
              {
                value: "lastMonth",
                label: "Last month",
              },
              {
                value: "lastYear",
                label: "Last year",
              },
            ]}
            disableRangeSelection={false}
            setIsValidRange={setIsValidRange}
          />
        </Box>

        <Box
          className="date-inputs-container"
          sx={
            hideDivider
              ? {}
              : {
                  paddingLeft: "20px",
                  borderLeft: "solid 1px #6B7280",
                }
          }
        >
          <DateRangePicker
            range={tempComparisonPeriodRange}
            setRange={setTempComparisonPeriodRange}
            period={comparePeriod}
            setPeriod={(period) =>
              onChangeCurrentPeriod("comparisonPeriodRange", period)
            }
            title={{
              label: "Comparison Period",
              markColor: "#111827",
            }}
            periodList={[
              {
                value: "custom",
                label: "Custom",
              },
              {
                value: "previousMouthToDate",
                label: (
                  <>
                    Previous month to date{" "}
                    <Typography
                      component="em"
                      sx={{
                        color: "#808080",
                        fontStyle: "italic",
                        fontSize: "12px",
                      }}
                    >
                      {" "}
                      (Default)
                    </Typography>
                  </>
                ),
              },
              {
                value: "previousWeek",
                label: "Previous week",
              },
              {
                value: "previousMouth",
                label: "Previous month",
              },
              {
                value: "previousYear",
                label: "Previous year",
              },
              {
                value: "none",
                label: "None",
              },
            ]}
            calendarClass="comparison-period-picker"
            disableRangeSelection={false}
            disableCalendar={comparePeriod === "none"}
            setIsValidRange={setIsValidRange}
          />
        </Box>
      </Stack>

      {isValidRange ? (
        !hidePeriodDiff &&
        comparePeriod !== "none" && (
          <Box sx={{ marginTop: "10px" }}>
            {currentPeriodRange && (
              <Typography
                component="span"
                sx={{ color: "#4A4D52", fontSize: "14px" }}
              >
                Comparing{" "}
                {moment(currentPeriodRange.endDate).diff(
                  moment(currentPeriodRange.startDate),
                  "days"
                ) + 1}{" "}
                days to{" "}
                {moment(comparisonPeriodRange.endDate).diff(
                  moment(comparisonPeriodRange.startDate),
                  "days"
                ) + 1}{" "}
                days
              </Typography>
            )}
          </Box>
        )
      ) : (
        <Box sx={{ marginTop: "10px" }}>
          <Typography
            component="span"
            sx={{ color: "#781818", fontSize: "14px" }}
          >
            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
            <Typography
              component="span"
              sx={{ marginLeft: "5px", fontSize: "14px" }}
            >
              Please enter a valid date
            </Typography>
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default PeriodPickers;
